// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : manage query params for TreeTable
// =============================================================================
// ---Third-party imports
// ---Framework imports
import log from 'uix/logging';
import { dict, size, isArray, items, isNil } from 'ldash';
import { useEffectSSR as useEffect, useRouter, useMemo, useCallback } from 'uix/hooks';
import { useChosenTypes } from 'uix/types/components';
import { buildReactTableOpts } from 'uix/components/table';
// ---Exports
export const useQueryParamOverrideList = (opts, setOpts)=>{
    /* query params -> opts of list data layer
   */ // handle these names + "types"
    let names = [
        'descendants',
        'subdomains',
        'links'
    ];
    let { request } = useRouter();
    let [types, setTypes] = useChosenTypes();
    let { descendants, subdomains, links } = opts;
    useEffect(()=>{
        // derive options from query params
        let params = dict(names.map((n)=>[
                n,
                request.query[n]
            ]).filter(([n, v])=>!isNil(v)));
        if (size(params) > 0) {
            setOpts(params);
        }
        // update types
        let t = request.query.types === null ? '' : request.query.types;
        if (!isNil(t)) {
            setTypes(t.split(',').filter((e)=>e));
        }
    }, [
        request
    ]);
    return useMemo(()=>({
            descendants,
            subdomains,
            links,
            types
        }), [
        descendants,
        subdomains,
        links,
        types
    ]);
};
export const useQueryParamOverride = (opts, setOpts, treeListOpts, rtOpts, activeColumnIds)=>{
    /* process opts from 3 layers:
  - treeListOpts: list data layer
  - opts: TreeTable layer
  - rtOpts: inner options of react table (also see components/table/index.js)
  return go action with persisted query params
  */ let { go, request } = useRouter();
    // for filtered + sorted see components/table/useQueryParamOverrideReactTable
    let names = [
        'showFooter',
        'filterable',
        'selectable',
        'groupBy',
        'cols'
    ];
    let goToPersistentLink = useCallback(()=>{
        let _rtOpts = buildReactTableOpts(activeColumnIds, rtOpts);
        let params = [
            ...names.map((n)=>[
                    n,
                    opts[n]
                ]),
            ...items(_rtOpts)
        ];
        if (treeListOpts) {
            params = [
                ...params,
                ...items(treeListOpts)
            ];
        }
        params = dict(params.map(([k, v])=>[
                k,
                isArray(v) ? v.join(',') : v
            ]));
        go({
            updateQuery: params
        });
    }, [
        activeColumnIds,
        opts,
        rtOpts,
        treeListOpts
    ]);
    let decode = (stored, queryval)=>{
        if (isArray(stored)) {
            if (queryval === null) {
                return [];
            }
            return queryval.split(',').filter((e)=>e);
        } else {
            return queryval;
        }
    };
    useEffect(()=>{
        // derive options from query params
        let params = dict(names.filter((n)=>request.query[n] !== undefined).map((n)=>[
                n,
                decode(opts[n], request.query[n])
            ]));
        if (size(params) > 0) {
            //~ log.x('derive table from query: %o', params)
            setOpts(params);
        }
    }, [
        request
    ]);
    // XXX: why should we cripple empty treeListOpts from CustomListTable (/import/oairecs)
    //~ if (!treeListOpts) {
    // we need a full configuration (all 3 option sets) for a usable link
    //~ goToPersistentLink = null
    //~ }
    return {
        goToPersistentLink
    };
};
